import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useState } from "react";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import editIcon from "../../assets/edit_icon.png";
import {
  createOrganizationBelonging,
  deleteOrganizationBelonging,
  getOrganizationBelongings,
  updateOrganizationBelonging,
} from "../../services/belongings/belongingsService";
import addIcon from "../../assets/add_icon_light.png";
import BelongingsStatusSelector from "../../components/BelongingStatusSelector";
import StatusBadge from "../../components/StatusBadge";
import deleteIcon from "../../assets/delete_Icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";
import OrganizationEmployeeSelector from "../../components/EmployeeOrganizationSelector";
import {
  RadioGroup,
  RadioGroupItem,
  RadioLabel,
  Radio,
} from "../../components/Radio";

const Belongings = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [belongings, setBelongings] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedBelonging, setSelectedBelonging] = useState(null);
  const [showEditAvailabilityModal, setShowEditAvailabilityModal] =
    useState(false);
  const [showWorkingStatusModal, setShowWorkingStatusModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const belongingStatusColorOptions = {
    AVAILABLE: "green",
    BORROWED: "orange",
    FUNCTIONAL: "blue",
    BROKEN: "red",
  };

  const tableTitles = [
    {
      keepOnMobile: true,
      value: "name",
    },
    {
      keepOnMobile: true,
      value: "workingStatus",
    },
    {
      keepOnMobile: true,
      value: "borrowedStatus",
    },
    {
      keepOnMobile: true,
      value: "user",
    },
    { value: "notes", keepOnMobile: true },
    {
      keepOnMobile: true,
      value: "Actions",
    },
  ];

  const fetchBelongings = async () => {
    setLoading(true);
    const data = await getOrganizationBelongings({
      page: pagination.currentPage,
    });
    setBelongings(data?.data?.organizationBelonging || []);
    setPagination((prev) => ({
      currentPage: data?.data?.currentPage,
      totalPages: data?.data?.totalPages,
      totalRecords: data?.data?.totalRecords,
    }));
    setLoading(false);
  };

  const tableRows = belongings?.map((belonging) => [
    {
      value: belonging?.name,
      type: TABLE_CELL_TYPES.ELEMENT,
      eclipseText: true,
      keepOnMobile: true,
    },
    {
      value: belonging.workingStatus && (
        <button
          className="unset-all t-cursor-pointer c-belonging-status-badge-button"
          onClick={() => {
            setSelectedBelonging(belonging);
            setShowWorkingStatusModal(true);
          }}
        >
          <StatusBadge
            title={belonging.workingStatus}
            color={belongingStatusColorOptions[belonging.workingStatus]}
          />
        </button>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: belonging.availabilityStatus && (
        <button
          className="unset-all t-cursor-pointer c-belonging-status-badge-button"
          onClick={() => {
            setSelectedBelonging(belonging);
            setShowEditAvailabilityModal(true);
          }}
        >
          <StatusBadge
            title={belonging.availabilityStatus}
            color={belongingStatusColorOptions[belonging.availabilityStatus]}
          />
        </button>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: belonging.user?.id
        ? belonging.user.username
        : t("belongingNotAssigned"),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: belonging?.notes,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          variant={"blank"}
          icon={editIcon}
          iconSize={18}
          onClick={() => {
            setSelectedBelonging({
              ...belonging,
            });
            setShowEditModal(true);
          }}
          title={t("edit")}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  useEffect(() => {
    fetchBelongings();
  }, [pagination.currentPage]);

  return (
    <PageWrapper
      style={{ direction: currentDirection }}
      title={t("belongings")}
      withBackButton
    >
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <div className="o-row o-row--horizontal-align_end">
          <CustomButton
            title={t("addNewBelonging")}
            icon={addIcon}
            iconSize={16}
            onClick={() => setShowAddModal(true)}
            style={{ marginStart: "auto" }}
          />
        </div>

        <section>
          {loading ? (
            <Loader size={LOADER_SIZES.MEDIUM} />
          ) : (
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          )}
        </section>
      </div>
      {showAddModal && (
        <AddNewBelongingModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            fetchBelongings();
            setShowAddModal(false);
          }}
        />
      )}
      {showEditModal && (
        <EditBelongingsModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          belonging={selectedBelonging}
          onSuccess={() => {
            fetchBelongings();
            setShowEditModal(false);
          }}
        />
      )}

      {showWorkingStatusModal && (
        <EditWorkingStatusModal
          open={showWorkingStatusModal}
          onClose={() => setShowWorkingStatusModal(false)}
          belonging={selectedBelonging}
          onSuccess={() => {
            fetchBelongings();
            setShowWorkingStatusModal(false);
          }}
        />
      )}

      {showEditAvailabilityModal && (
        <EditAvailabilityModal
          open={showEditAvailabilityModal}
          onClose={() => setShowEditAvailabilityModal(false)}
          belonging={selectedBelonging}
          onSuccess={() => {
            fetchBelongings();
            setShowEditAvailabilityModal(false);
          }}
        />
      )}
    </PageWrapper>
  );
};

const EditBelongingsModal = ({ open, onClose, onSuccess, belonging }) => {
  const { t } = useTranslation("general");
  const [belongingData, setBelongingData] = useState({
    name: belonging?.name,
    notes: belonging?.notes || "",
    workingStatus: belonging?.workingStatus ?? "FUNCTIONAL",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setBelongingData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateName = (value) => {
    if (!value || value.trim() === "") {
      return t("nameRequired");
    }
    return "";
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const nameError = validateName(belongingData.name);
    if (nameError) newErrors.name = nameError;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      setErrors({});
      setIsSubmitting(true);
      await updateOrganizationBelonging(belonging.id, {
        name: belongingData.name,
        workingStatus: belongingData.workingStatus,
        notes: belongingData.notes,
      });

      onClose();
      onSuccess?.();
    } catch (error) {
      setErrors({
        submit: t("errorUpdatingBelonging"),
      });
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBelonging = async (belongingId) => {
    try {
      setIsSubmitting(true);
      const response = await deleteOrganizationBelonging(belongingId);

      if (response.status === 200) {
        onClose();
        onSuccess?.();
      } else {
        setErrors({
          submit: t("errorDeletingBelonging"),
        });
      }
    } catch (error) {
      setErrors({
        submit: t("errorDeletingBelonging"),
      });
      console.error("Error deleting belonging:", error);
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const formError =
    Object.keys(errors).length > 0
      ? errors.submit
        ? errors.submit
        : t("inputsCheckError")
      : null;

  return (
    <Modal
      title={t("editBelonging")}
      onClose={onClose}
      open={open}
      error={formError}
      footer={
        <section className="edit-user-actions">
          <CustomButton
            title={isSubmitting ? t("saving") : t("confirm")}
            icon={saveIcon}
            iconSize={16}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
          <CustomButton
            type="button"
            title={t("cancel")}
            variant="blank"
            icon={closeIcon}
            iconSize={14}
            onClick={onClose}
            disabled={isSubmitting}
          />
          <CustomButton
            type="button"
            title={t("delete")}
            variant="red"
            icon={deleteIcon}
            iconSize={14}
            onClick={() => setShowDeleteConfirmation(true)}
            disabled={isSubmitting}
          />
        </section>
      }
    >
      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() => handleDeleteBelonging(belonging.id)}
          customMessage={t("deleteBelongingConfirmation")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirmIconType="whiteCheck"
          cancel
        />
      )}
      {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
      <section>
        {formError && <ErrorBox>{formError}</ErrorBox>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="o-column o-column--gap-size_500">
            <div className="o-column o-column--gap-size_200">
              <span>{t("name")}: </span>
              {errors.name && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.name}
                </span>
              )}
              <CustomInput
                type="text"
                min="1"
                value={belongingData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                style={{
                  borderColor: errors.name ? "red" : undefined,
                }}
              />
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("workingStatus")}: </span>
              {errors.workingStatus && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.workingStatus}
                </span>
              )}
              <RadioGroup style={{ flexDirection: "row" }}>
                <RadioGroupItem>
                  <Radio
                    id="working-status-functional"
                    name="working-status-functional"
                    value="FUNCTIONAL"
                    checked={belongingData.workingStatus === "FUNCTIONAL"}
                    onChange={(e) =>
                      handleInputChange("workingStatus", e.target.value)
                    }
                  />
                  <RadioLabel htmlFor="working-status-functional">
                    {t("FUNCTIONAL")}
                  </RadioLabel>
                </RadioGroupItem>

                <RadioGroupItem>
                  <Radio
                    id="working-status-broken"
                    name="working-status"
                    value="BROKEN"
                    checked={belongingData.workingStatus === "BROKEN"}
                    onChange={(e) =>
                      handleInputChange("workingStatus", e.target.value)
                    }
                  />
                  <RadioLabel htmlFor="working-status-broken">
                    {t("BROKEN")}
                  </RadioLabel>
                </RadioGroupItem>
              </RadioGroup>
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("notes")}: </span>
              <CustomInput
                type="textarea"
                min="1"
                value={belongingData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                style={{
                  borderColor: errors.notes ? "red" : undefined,
                }}
              />
            </div>
          </div>
        </form>
      </section>
      {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
    </Modal>
  );
};

const AddNewBelongingModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation("general");
  const [belongingData, setBelongingData] = useState({
    name: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setBelongingData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateName = (value) => {
    if (!value || value.trim() === "") {
      return t("nameRequired");
    }
    return "";
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const nameError = validateName(belongingData.name);
    if (nameError) newErrors.name = nameError;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      setErrors({});
      setIsSubmitting(true);

      await createOrganizationBelonging({
        name: belongingData.name,
        notes: belongingData.notes,
      });

      onClose();
      onSuccess?.();
    } catch (error) {
      setErrors({
        submit: t("errorCreatingBelonging"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formError =
    Object.keys(errors).length > 0
      ? errors.submit
        ? errors.submit
        : t("inputsCheckError")
      : null;

  return (
    <Modal
      title={t("addNewBelonging")}
      onClose={onClose}
      open={open}
      error={formError}
      footer={
        <section className="edit-user-actions">
          <CustomButton
            title={isSubmitting ? t("saving") : t("confirm")}
            icon={saveIcon}
            iconSize={16}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
          <CustomButton
            type="button"
            title={t("cancel")}
            variant="blank"
            icon={closeIcon}
            iconSize={14}
            onClick={onClose}
            disabled={isSubmitting}
          />
        </section>
      }
    >
      {isSubmitting && <Loader size={LOADER_SIZES.SMALL} />}

      <section>
        {formError && <ErrorBox>{formError}</ErrorBox>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="o-column o-column--gap-size_500">
            <div className="o-column o-column--gap-size_200">
              <span>{t("name")}: </span>
              {errors.name && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.name}
                </span>
              )}
              <CustomInput
                type="text"
                value={belongingData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                style={{
                  borderColor: errors.name ? "red" : undefined,
                }}
              />
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("notes")}: </span>
              <CustomInput
                type="textarea"
                value={belongingData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                style={{
                  borderColor: errors.notes ? "red" : undefined,
                }}
              />
            </div>
          </div>
        </form>
      </section>
    </Modal>
  );
};

const EditAvailabilityModal = ({ open, onClose, onSuccess, belonging }) => {
  const { t } = useTranslation("general");
  const [belongingData, setBelongingData] = useState({
    availabilityStatus: belonging?.availabilityStatus ?? "AVAILABLE",
    userId: belonging?.userId || null,
    notes: belonging?.notes || "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setBelongingData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle availability status change
  const handleAvailabilityStatusChange = (status) => {
    setBelongingData((prev) => ({
      ...prev,
      availabilityStatus: status,
      // Clear userId when status is AVAILABLE
      userId: status === "AVAILABLE" ? null : prev.userId,
    }));

    // Clear errors
    if (errors.availabilityStatus) {
      setErrors((prev) => ({
        ...prev,
        availabilityStatus: "",
        userId: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // If status is BORROWED, userId is required
    if (
      belongingData.availabilityStatus === "BORROWED" &&
      !belongingData.userId
    ) {
      newErrors.userId = t("userRequiredForBorrowed");
      newErrors.submit = t("userRequiredForBorrowed");
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      setErrors({});
      setIsSubmitting(true);
      await updateOrganizationBelonging(belonging.id, {
        availabilityStatus: belongingData.availabilityStatus,
        userId: belongingData.userId,
        notes: belongingData.notes,
      });

      onClose();
      onSuccess?.();
    } catch (error) {
      console.log(error);
      setErrors({
        submit: t("errorUpdatingBorrowed"),
      });
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const formError =
    Object.keys(errors).length > 0
      ? errors.submit
        ? errors.submit
        : t("inputsCheckError")
      : null;

  const isUserSelectorDisabled =
    belongingData.availabilityStatus === "AVAILABLE";

  return (
    <Modal
      title={t("updateAvailability")}
      onClose={onClose}
      open={open}
      error={formError}
      footer={
        <section className="edit-user-actions">
          <CustomButton
            title={isSubmitting ? t("saving") : t("confirm")}
            icon={saveIcon}
            iconSize={16}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
          <CustomButton
            type="button"
            title={t("cancel")}
            variant="blank"
            icon={closeIcon}
            iconSize={14}
            onClick={onClose}
            disabled={isSubmitting}
          />
        </section>
      }
    >
      {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
      <section>
        {formError && <ErrorBox>{formError}</ErrorBox>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="o-column o-column--gap-size_500">
            <div className="o-column o-column--gap-size_200">
              <span>{t("availabilityStatus")}: </span>
              {errors.availabilityStatus && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.availabilityStatus}
                </span>
              )}
              <RadioGroup style={{ flexDirection: "row" }}>
                <RadioGroupItem>
                  <Radio
                    id="availability-status-available"
                    name="availability-status-available"
                    value="AVAILABLE"
                    checked={belongingData.availabilityStatus === "AVAILABLE"}
                    onChange={(e) =>
                      handleAvailabilityStatusChange(e.target.value)
                    }
                  />
                  <RadioLabel htmlFor="availability-status-available">
                    {t("available")}
                  </RadioLabel>
                </RadioGroupItem>

                <RadioGroupItem>
                  <Radio
                    id="availability-status-borrowed"
                    name="availability-status-borrowed"
                    value="BORROWED"
                    checked={belongingData.availabilityStatus === "BORROWED"}
                    onChange={(e) =>
                      handleAvailabilityStatusChange(e.target.value)
                    }
                  />
                  <RadioLabel htmlFor="availability-status-borrowed">
                    {t("borrowed")}
                  </RadioLabel>
                </RadioGroupItem>
              </RadioGroup>
            </div>

            <div
              className="o-column o-column--gap-size_200"
              style={{
                opacity: isUserSelectorDisabled ? 0.5 : 1,
                pointerEvents: isUserSelectorDisabled ? "none" : "auto",
                borderColor: errors.userId ? "red" : undefined,
              }}
            >
              <span>{t("user")}: </span>
              {errors.userId && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.userId}
                </span>
              )}
              <OrganizationEmployeeSelector
                value={belongingData.userId}
                onChange={(id) => {
                  console.log(id);
                  if (id === null) {
                    handleInputChange("userId", null);
                    return;
                  }

                  if (belongingData.availabilityStatus === "AVAILABLE") {
                    setErrors((prev) => ({
                      ...prev,
                      submit: t("cannotAssignUserToAvailableItem"),
                    }));
                    return;
                  }

                  handleInputChange("userId", id);
                }}
              />
            </div>

            <div className="o-column o-column--gap-size_200">
              <span>{t("notes")}: </span>
              <CustomInput
                type="textarea"
                min="1"
                value={belongingData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                style={{
                  borderColor: errors.notes ? "red" : undefined,
                }}
              />
            </div>
          </div>
        </form>
      </section>
      {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
    </Modal>
  );
};

const EditWorkingStatusModal = ({ open, onClose, onSuccess, belonging }) => {
  const { t } = useTranslation("general");
  const [belongingData, setBelongingData] = useState({
    workingStatus: belonging?.workingStatus ?? "WORKING",
    notes: belonging?.notes || "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle working status change
  const handleWorkingStatusChange = (status) => {
    setBelongingData((prev) => ({
      ...prev,
      workingStatus: status,
    }));

    // Clear errors
    if (errors.workingStatus) {
      setErrors((prev) => ({
        ...prev,
        workingStatus: "",
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setBelongingData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!belongingData.workingStatus) {
      newErrors.workingStatus = t("workingStatusRequired");
      newErrors.submit = t("workingStatusRequired");
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    console.log(belonging);
    e.preventDefault();
    setIsSubmitting(true);

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      setErrors({});
      setIsSubmitting(true);
      await updateOrganizationBelonging(belonging.id, {
        workingStatus: belongingData.workingStatus,
        notes: belongingData.notes,
      });

      onClose();
      onSuccess?.();
    } catch (error) {
      console.log(error);
      setErrors({
        submit: t("errorUpdatingWorkingStatus"),
      });
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const formError =
    Object.keys(errors).length > 0
      ? errors.submit
        ? errors.submit
        : t("inputsCheckError")
      : null;

  return (
    <Modal
      title={t("updateWorkingStatus")}
      onClose={onClose}
      open={open}
      error={formError}
      footer={
        <section className="edit-user-actions">
          <CustomButton
            title={isSubmitting ? t("saving") : t("confirm")}
            icon={saveIcon}
            iconSize={16}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
          <CustomButton
            type="button"
            title={t("cancel")}
            variant="blank"
            icon={closeIcon}
            iconSize={14}
            onClick={onClose}
            disabled={isSubmitting}
          />
        </section>
      }
    >
      {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
      <section>
        {formError && <ErrorBox>{formError}</ErrorBox>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="o-column o-column--gap-size_500">
            <div className="o-column o-column--gap-size_200">
              <span>{t("workingStatus")}: </span>
              {errors.workingStatus && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.workingStatus}
                </span>
              )}
              <RadioGroup style={{ flexDirection: "row" }}>
                <RadioGroupItem>
                  <Radio
                    id="working-status-functional"
                    name="working-status-functional"
                    value="FUNCTIONAL"
                    checked={belongingData.workingStatus === "FUNCTIONAL"}
                    onChange={(e) => handleWorkingStatusChange(e.target.value)}
                  />
                  <RadioLabel htmlFor="working-status-functional">
                    {t("FUNCTIONAL")}
                  </RadioLabel>
                </RadioGroupItem>

                <RadioGroupItem>
                  <Radio
                    id="working-status-broken"
                    name="working-status-broken"
                    value="BROKEN"
                    checked={belongingData.workingStatus === "BROKEN"}
                    onChange={(e) => handleWorkingStatusChange(e.target.value)}
                  />
                  <RadioLabel htmlFor="working-status-broken">
                    {t("BROKEN")}
                  </RadioLabel>
                </RadioGroupItem>
              </RadioGroup>
            </div>
            <div className="o-column o-column--gap-size_200">
              <span>{t("notes")}: </span>
              <CustomInput
                type="textarea"
                min="1"
                value={belongingData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                style={{
                  borderColor: errors.notes ? "red" : undefined,
                }}
              />
            </div>
          </div>
        </form>
      </section>
      {isSubmitting ? <Loader size={LOADER_SIZES.SMALL} /> : null}
    </Modal>
  );
};

export default Belongings;
